/* TrackerBase App Calendar Global View service
 *
 * Angular Module: trackerbase-app.services.calendar-global-view
 * Injected Service: CalendarGlobalViewService
 *
 */

import angular from "angular";
import Assert from "tbAssert";
import AllEventTypesServiceModule from "tbClient/services/AllEventTypesService";
import UserInfoServiceModule from "tbClient/services/UserInfoService";
import angularStorage from "angular-storage";

import { GlobalViewDefinition } from "tbClient/models/GlobalView";
import { GlobalViewNames } from "tbClient/services/WellKnownGlobalViews";

const mLogSvc = new WeakMap();
const mStorageSvc = new WeakMap();
const mAllEventTypesSvc = new WeakMap();
const mUserInfoSvc = new WeakMap();

const mRegisteredGlobalViews = new WeakMap();
const mCurrentGlobalView = new WeakMap();
const mGlobalViewsLoaded = new WeakMap();
const mUserGlobalViewPrefsKey = new WeakMap();
const mCanStoreGlobalViewPrefs = new WeakMap();

const _mmEventTypeName = "Media Monitoring";
const _defaultGlobalViewName = GlobalViewNames.tracking;
const _defaultStaticGlobalViewName = GlobalViewNames.none;
const _unknownUserPrefsKey = "unknownUser1221";

// "None" Global View Definition - doesn't have any effect and thus has no data dependencies
const _noneGlobalViewDefinition = new GlobalViewDefinition({
    viewName: GlobalViewNames.none,
    viewFriendlyName: "None",
    viewDescription: "Show tracking and media monitoring events",
    viewRule: (evtFilter) => evtFilter, // event filter unmodified
    // eslint-disable-next-line no-unused-vars
    viewClientRule: (evt) => true // all events pass
});

class CalendarGlobalViewService {
    constructor($log, store, AllEventTypesService, UserInfoService) {
        Assert.StrictAssert($log != null, "Missing Log Service Dependency");
        Assert.StrictAssert(store != null, "Missing Storage Service Dependency");
        Assert.StrictAssert(AllEventTypesService != null, "Missing event types service");
        Assert.StrictAssert(UserInfoService != null, "Missing User Info Service Dependency");

        mLogSvc.set(this, $log);
        mAllEventTypesSvc.set(this, AllEventTypesService);
        mStorageSvc.set(this, store);
        mUserInfoSvc.set(this, UserInfoService);

        mCanStoreGlobalViewPrefs.set(this, false);
        mUserGlobalViewPrefsKey.set(this, _unknownUserPrefsKey);

        mCurrentGlobalView.set(this, null);
        mRegisteredGlobalViews.set(this, new Map());
        mGlobalViewsLoaded.set(this, false);
    }

    _getNonDataDependentGlobalViews() {
        return [
            _noneGlobalViewDefinition
        ];
    }

    _loadSavedViewName() {
        const userName = mUserInfoSvc.get(this).userName;
        if (userName == null) {
            mUserGlobalViewPrefsKey.set(this, _unknownUserPrefsKey);
            mCanStoreGlobalViewPrefs.set(this, false);
            return null;
        }

        const userGlobalViewsPrefsKey = `${userName}.globalView`;
        mUserGlobalViewPrefsKey.set(this, userGlobalViewsPrefsKey);
        mCanStoreGlobalViewPrefs.set(this, true);

        const savedGlobalViewName =  mStorageSvc.get(this).get(userGlobalViewsPrefsKey);
        if (typeof savedGlobalViewName === "string") {
            return savedGlobalViewName;
        }
        return null;
    }

    _saveCurrentViewName() {
        const logSvc = mLogSvc.get(this);
        if (mCanStoreGlobalViewPrefs.get(this) === true) {
            const userGlobalViewsPrefsKey = mUserGlobalViewPrefsKey.get(this);
            const currentGlobalViewName = mCurrentGlobalView.get(this).name;
            mStorageSvc.get(this).set(userGlobalViewsPrefsKey, currentGlobalViewName);
            logSvc.debug(`Saved current global view name (${currentGlobalViewName})`);
        }
        else {
            logSvc.debug("Cannot currently save global view name");
        }
    }

    reload() {
        const eventTypesSvc = mAllEventTypesSvc.get(this);
        const logSvc = mLogSvc.get(this);
        logSvc.debug("Global View Service: initializing");

        const _registeredGlobalViews = [];
        _registeredGlobalViews.push(...this._getNonDataDependentGlobalViews());

        const mmEventType = eventTypesSvc.FromName(_mmEventTypeName);
        if (mmEventType != null) {
            _registeredGlobalViews.push(
                new GlobalViewDefinition({
                    viewName: GlobalViewNames.tracking,
                    viewFriendlyName: "Tracking",
                    viewDescription: "Only show tracking events",
                    //viewRule: EventFilter transform function
                    viewRule: (evtFilter /*EventFilter*/) => {
                        if (!evtFilter.excludeTypeFilter.includes(mmEventType.eventTypeId)) {
                            evtFilter.excludeTypeFilter.push(mmEventType.eventTypeId);
                        }
                        return evtFilter;
                    },
                    viewClientRule: (evt) => {
                        // events pass if none of their event types is the mm event type
                        return evt.eventTypes.every(et => et.eventTypeId !== mmEventType.eventTypeId);
                    }
                }),
                new GlobalViewDefinition({
                    viewName: GlobalViewNames.media_monitoring,
                    viewFriendlyName: "Media Monitoring",
                    viewDescription: "Only show media monitoring events",
                    viewRule: (evtFilter /*EventFilter*/) => {
                        if (!evtFilter.includeTypeFilter.includes(mmEventType.eventTypeId)) {
                            evtFilter.includeTypeFilter.push(mmEventType.eventTypeId);
                        }
                        return evtFilter;
                    },
                    viewClientRule: (evt) => {
                        if (evt.eventTypes.length === 0) { return true; }
                        // Avoid odd behavior if newly created mm events got insta-filtered.
                        // Search for the mm event type in the event's list of event types,
                        // Event passes if the mm event type was found
                        const idxOfMmEtype = evt.eventTypes.findIndex(et => et.eventTypeId === mmEventType.eventTypeId);
                        return idxOfMmEtype !== -1;
                    }
                })
            );
        }
        else {
            logSvc.debug("GlobalViewService: event types not loaded, not adding data-dependent views");
        }
        const registeredGlobalViews = mRegisteredGlobalViews.get(this);
        registeredGlobalViews.clear();
        _registeredGlobalViews.forEach((gf) => registeredGlobalViews.set(gf.name, gf));

        // const currentView = this.getCurrentView();
        let currentViewName = this._loadSavedViewName();
        if (currentViewName == null || !(registeredGlobalViews.has(currentViewName))) {
            logSvc.debug(
                `GlobalViewService.reload: Stored current view name null or not currently available: ${currentViewName != null ? currentViewName : "(null)"}`
            );
            currentViewName = _defaultGlobalViewName;
        }
        const globalViewChangedOk = this._changeCurrentViewImpl(currentViewName);
        if (!globalViewChangedOk) {
            logSvc.debug("Falling back to baseline global view");
            this._changeCurrentViewImpl(_defaultStaticGlobalViewName);
        }
        logSvc.debug("Global View Service: initialized");
    }

    get availableGlobalViews() {
        const registeredGlobalViews = mRegisteredGlobalViews.get(this);
        return (registeredGlobalViews != null) ?
            registeredGlobalViews.values().map((gvDef) => gvDef.viewInfo()) :
            [];
    }

    getCurrentView() {
        return mCurrentGlobalView.get(this);
    }

    _changeCurrentViewImpl(viewName) {
        const selectedView = mRegisteredGlobalViews.get(this).get(viewName);
        if (selectedView !== null && selectedView !== undefined) {
            mCurrentGlobalView.set(this, selectedView);
            mLogSvc.get(this).debug(`Changed Global View to ${selectedView.name}`);
            this._saveCurrentViewName();
            return true;
        }
        return false;
    }

    changeCurrentView(viewName) {
        const currentViewChangedOk = this._changeCurrentViewImpl(viewName);
        if (!currentViewChangedOk) {
            mLogSvc.get(this).error("Unable to change global view: selection either doesn't exist or has no definition");
        }
    }
}

CalendarGlobalViewService.$inject = [
    "$log", "store", "AllEventTypesService", "UserInfoService"
];

export default angular
    .module("trackerbase-app.services.calendar-global-view",[
        AllEventTypesServiceModule,
        UserInfoServiceModule,
        angularStorage
    ])
    .service("CalendarGlobalViewService", CalendarGlobalViewService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/services/CalendarGlobalViewService.js
// module id = SO06
// module chunks = 1